import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { router } from './router';
import vuetify from './plugins/vuetify';
import '@/scss/style.scss';
import PerfectScrollbar from 'vue3-perfect-scrollbar';
import VueApexCharts from 'vue3-apexcharts';
import VueTablerIcons from 'vue-tabler-icons';
import 'vue3-carousel/dist/carousel.css';
import 'shepherd.js/dist/css/shepherd.css';
import Maska from 'maska';
// print
// import print from 'vue3-print-nb';
// Table
import Vue3EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import Vue3Lottie from 'vue3-lottie';
//i18
import { createI18n } from 'vue-i18n';
import messages from '@/utils/locales/messages';
//ScrollTop
import VueScrollTo from 'vue-scrollto';

import currencyPlugin from './plugins/currencyPlugin';
import { vCurrency } from '@/utils/directive/v-currency';

import InputLabel from './components/common/input/InputLabel.vue';
import InputLoadingIcon from './components/common/input/InputLoadingIcon.vue';

import { trackAction } from '@/utils/directive/trackAction';   // [Metric]
import projectConfig from "@/config/appConfig";
import { updatePageTitle, updateFavicon, getTypographyConfig, getFontFamily } from '@/config/configHelpers';

const { config } = projectConfig

// Importar Sentry
import * as Sentry from '@sentry/vue';

const i18n = createI18n({
    locale: 'en',
    messages: messages,
    silentTranslationWarn: true,
    silentFallbackWarn: true
});

// Crear instancia de MSAL
// const msalInstance = new PublicClientApplication(msalConfig);

const app = createApp(App);

// Inicializar Sentry
Sentry.init({
    app,
    dsn: 'https://af39a440ea29af1e2fd9f009b92f805e@o4510432641482752.ingest.us.sentry.io/4510466342453248',
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Captura el 100% de las transacciones
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% de las sesiones
    replaysOnErrorSampleRate: 1.0, // 100% cuando hay errores
    // Enviar información PII (IP address, etc)
    sendDefaultPii: true,
    // Configuración de entorno
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD, // Solo habilitado en producción
});

// Función de prueba de Sentry (solo en desarrollo)
if (import.meta.env.DEV) {
    (window as any).testSentry = () => {
        console.log('🧪 Probando Sentry...');
        // @ts-ignore - Error intencional para probar Sentry
        myUndefinedFunction();
    };
    console.log('💡 Sentry configurado. Prueba ejecutando: window.testSentry()');
}

// Configurar usuario en Sentry si ya existe sesión activa
if (import.meta.env.PROD) {
    const userStr = localStorage.getItem('user');
    if (userStr) {
        try {
            const user = JSON.parse(userStr);
            Sentry.setUser({
                id: user.id?.toString(),
                email: user.email,
                username: `${user.first_name} ${user.last_name}`
            });
        } catch (error) {
            console.error('Error al configurar usuario en Sentry:', error);
        }
    }
}

const pinia = createPinia();

app.provide("appConfig", config);
app.directive('track-action', trackAction);
app.use(pinia);
app.use(router);
app.component('EasyDataTable', Vue3EasyDataTable);
app.use(PerfectScrollbar);
app.use(VueTablerIcons);
// app.use(print);
app.use(i18n);
app.use(Maska);
app.use(VueApexCharts);
app.use(Vue3Lottie);
//ScrollTop Use
// app.use(VueScrollTo);
app.use(VueScrollTo, {
    duration: 1000,
    easing: 'ease',
    offset: -50
});
// app.provide('msalInstance', msalInstance);
app.use(currencyPlugin);
app.directive('currency', vCurrency);
app.component('InputLabel', InputLabel);
app.component('InputLoadingIcon', InputLoadingIcon);

updatePageTitle();
updateFavicon();

// Aplicar fuente dinámicamente desde la configuración
const applyFontFamily = () => {
  const typography = getTypographyConfig();
  const fontFamily = getFontFamily();
  
  // Aplicar fuente al root y body
  document.documentElement.style.setProperty('--app-font-family', fontFamily);
  document.documentElement.style.fontFamily = fontFamily;
  if (document.body) {
    document.body.style.fontFamily = fontFamily;
  }
  
  // Cargar Google Fonts si está configurado
  if (typography.isGoogleFont && typography.googleFontsUrl) {
    // Verificar si ya existe el link
    const existingLink = document.querySelector(`link[href="${typography.googleFontsUrl}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = typography.googleFontsUrl;
      document.head.appendChild(link);
    }
  }
};

// Aplicar fuente al iniciar
applyFontFamily();

app.use(vuetify).mount('#app');

